import type { JSX } from 'solid-js';

export type GlyphName =
  | 'map'
  | 'jobs'
  | 'deck'
  | 'profile'
  | 'log'
  | 'target'
  | 'drone'
  | 'biz'
  | 'net'
  | 'cpu'
  | 'signal'
  | 'alert'
  | 'lock'
  | 'unlock'
  | 'chip'
  | 'pkt'
  | 'arrow'
  | 'plus'
  | 'x'
  | 'chev'
  | 'coin'
  | 'rep'
  | 'skill';

interface GlyphProps {
  name: GlyphName;
  size?: number;
  color?: string;
  class?: string;
  style?: JSX.CSSProperties | string;
}

/** Tiny mono SVG glyph set. Functional geometry, no decorative shapes.
 *  Ports docs/design/handoff/shared.jsx Glyph component to SolidJS. */
export function Glyph(props: GlyphProps) {
  const size = () => props.size ?? 14;
  const color = () => props.color ?? 'currentColor';
  const paint = () => {
    const c = color();
    switch (props.name) {
      case 'map':
        return (
          <g>
            <rect x="2" y="3" width="6" height="14" />
            <rect x="9" y="5" width="5" height="14" />
            <rect x="15" y="3" width="6" height="14" />
          </g>
        );
      case 'jobs':
        return (
          <g>
            <rect x="3" y="5" width="16" height="2" />
            <rect x="3" y="10" width="16" height="2" />
            <rect x="3" y="15" width="10" height="2" />
          </g>
        );
      case 'deck':
        return (
          <g>
            <rect x="3" y="3" width="7" height="7" />
            <rect x="12" y="3" width="7" height="7" />
            <rect x="3" y="12" width="7" height="7" />
            <rect x="12" y="12" width="7" height="7" />
          </g>
        );
      case 'profile':
        return (
          <g>
            <circle cx="11" cy="8" r="3.5" fill="none" stroke={c} stroke-width="1.6" />
            <path d="M4 19c1.5-3.5 4-5 7-5s5.5 1.5 7 5" fill="none" stroke={c} stroke-width="1.6" />
          </g>
        );
      case 'log':
        return (
          <g>
            <rect x="3" y="4" width="2" height="2" />
            <rect x="7" y="4" width="12" height="2" />
            <rect x="3" y="9" width="2" height="2" />
            <rect x="7" y="9" width="9" height="2" />
            <rect x="3" y="14" width="2" height="2" />
            <rect x="7" y="14" width="14" height="2" />
          </g>
        );
      case 'target':
        return (
          <g>
            <circle cx="11" cy="11" r="7" fill="none" stroke={c} stroke-width="1.5" />
            <circle cx="11" cy="11" r="3" fill="none" stroke={c} stroke-width="1.5" />
            <line x1="11" y1="1" x2="11" y2="4" stroke={c} stroke-width="1.5" />
            <line x1="11" y1="18" x2="11" y2="21" stroke={c} stroke-width="1.5" />
            <line x1="1" y1="11" x2="4" y2="11" stroke={c} stroke-width="1.5" />
            <line x1="18" y1="11" x2="21" y2="11" stroke={c} stroke-width="1.5" />
          </g>
        );
      case 'drone':
        return (
          <g>
            <polygon points="11,3 17,11 11,19 5,11" fill="none" stroke={c} stroke-width="1.5" />
            <circle cx="11" cy="11" r="1.5" />
          </g>
        );
      case 'biz':
        return (
          <g>
            <rect x="3" y="6" width="16" height="13" fill="none" stroke={c} stroke-width="1.5" />
            <rect x="6" y="3" width="10" height="3" fill="none" stroke={c} stroke-width="1.5" />
          </g>
        );
      case 'net':
        return (
          <g>
            <circle cx="11" cy="6" r="2" />
            <circle cx="5" cy="16" r="2" />
            <circle cx="17" cy="16" r="2" />
            <line x1="11" y1="8" x2="5" y2="14" stroke={c} stroke-width="1.4" />
            <line x1="11" y1="8" x2="17" y2="14" stroke={c} stroke-width="1.4" />
          </g>
        );
      case 'cpu':
        return (
          <g>
            <rect x="6" y="6" width="10" height="10" fill="none" stroke={c} stroke-width="1.5" />
            <rect x="9" y="9" width="4" height="4" />
            <line x1="3" y1="8" x2="6" y2="8" stroke={c} stroke-width="1.4" />
            <line x1="3" y1="14" x2="6" y2="14" stroke={c} stroke-width="1.4" />
            <line x1="16" y1="8" x2="19" y2="8" stroke={c} stroke-width="1.4" />
            <line x1="16" y1="14" x2="19" y2="14" stroke={c} stroke-width="1.4" />
            <line x1="8" y1="3" x2="8" y2="6" stroke={c} stroke-width="1.4" />
            <line x1="14" y1="3" x2="14" y2="6" stroke={c} stroke-width="1.4" />
            <line x1="8" y1="16" x2="8" y2="19" stroke={c} stroke-width="1.4" />
            <line x1="14" y1="16" x2="14" y2="19" stroke={c} stroke-width="1.4" />
          </g>
        );
      case 'signal':
        return (
          <g>
            <rect x="2" y="14" width="3" height="5" />
            <rect x="7" y="10" width="3" height="9" />
            <rect x="12" y="6" width="3" height="13" />
            <rect x="17" y="2" width="3" height="17" />
          </g>
        );
      case 'alert':
        return (
          <g>
            <polygon points="11,3 20,18 2,18" fill="none" stroke={c} stroke-width="1.6" />
            <rect x="10" y="8" width="2" height="6" />
            <rect x="10" y="15" width="2" height="2" />
          </g>
        );
      case 'lock':
        return (
          <g>
            <rect x="5" y="10" width="12" height="9" fill="none" stroke={c} stroke-width="1.5" />
            <path d="M7 10V7a4 4 0 018 0v3" fill="none" stroke={c} stroke-width="1.5" />
          </g>
        );
      case 'unlock':
        return (
          <g>
            <rect x="5" y="10" width="12" height="9" fill="none" stroke={c} stroke-width="1.5" />
            <path d="M7 10V7a4 4 0 017-2.8" fill="none" stroke={c} stroke-width="1.5" />
          </g>
        );
      case 'chip':
        return (
          <g>
            <rect x="4" y="4" width="14" height="14" fill="none" stroke={c} stroke-width="1.5" />
            <line x1="8" y1="4" x2="8" y2="18" stroke={c} stroke-width="1" />
            <line x1="14" y1="4" x2="14" y2="18" stroke={c} stroke-width="1" />
          </g>
        );
      case 'pkt':
        return (
          <g>
            <rect x="2" y="9" width="4" height="4" />
            <rect x="9" y="9" width="4" height="4" />
            <rect x="16" y="9" width="4" height="4" />
          </g>
        );
      case 'arrow':
        return (
          <g>
            <path
              d="M5 11h11M11 6l5 5-5 5"
              fill="none"
              stroke={c}
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        );
      case 'plus':
        return (
          <g>
            <line x1="11" y1="4" x2="11" y2="18" stroke={c} stroke-width="1.6" stroke-linecap="round" />
            <line x1="4" y1="11" x2="18" y2="11" stroke={c} stroke-width="1.6" stroke-linecap="round" />
          </g>
        );
      case 'x':
        return (
          <g>
            <line x1="5" y1="5" x2="17" y2="17" stroke={c} stroke-width="1.6" stroke-linecap="round" />
            <line x1="17" y1="5" x2="5" y2="17" stroke={c} stroke-width="1.6" stroke-linecap="round" />
          </g>
        );
      case 'chev':
        return (
          <g>
            <path
              d="M8 5l6 6-6 6"
              fill="none"
              stroke={c}
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        );
      case 'coin':
        return (
          <g>
            <circle cx="11" cy="11" r="7" fill="none" stroke={c} stroke-width="1.5" />
            <path
              d="M11 6v10M8 8h5a2 2 0 010 4H9a2 2 0 000 4h5"
              fill="none"
              stroke={c}
              stroke-width="1.3"
            />
          </g>
        );
      case 'rep':
        return (
          <g>
            <polygon
              points="11,3 13.5,8.5 19,9 14.5,13 16,19 11,16 6,19 7.5,13 3,9 8.5,8.5"
              fill="none"
              stroke={c}
              stroke-width="1.4"
            />
          </g>
        );
      case 'skill':
        return (
          <g>
            <circle cx="11" cy="11" r="6" fill="none" stroke={c} stroke-width="1.4" />
            <circle cx="11" cy="11" r="2.5" />
          </g>
        );
    }
  };
  return (
    <svg
      viewBox="0 0 22 22"
      width={size()}
      height={size()}
      fill={color()}
      class={props.class}
      style={typeof props.style === 'string' ? props.style : { display: 'block', ...(props.style ?? {}) }}
    >
      {paint()}
    </svg>
  );
}
